/**
 * SyncToThaklisDialog Component
 * Dialog for syncing payment to Thaklis
 *
 * Invoice-based flow (recommended):
 *   - If payment has invoice_id, shows invoice confirmation
 *   - Tickets are automatically taken from invoice line items
 *   - No manual ticket selection needed
 *
 * Manual ticket selection (fallback):
 *   - If payment has no invoice, allows manual ticket selection
 *   - Supports partial payments
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Sync as SyncIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';

const SyncToThaklisDialog = ({ open, onClose, payment, onSyncComplete }) => {
  const [unpaidTickets, setUnpaidTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const paymentAmount = parseFloat(payment?.amount || 0);
  const hasInvoice = !!payment?.invoice_id;

  // Get line items from invoice (API returns 'lineItems' camelCase)
  const invoiceLineItems = invoiceDetails?.lineItems || invoiceDetails?.line_items || [];

  // For invoice-based: ticket total from invoice
  // For manual: sum of selected tickets
  const ticketTotal = hasInvoice
    ? invoiceLineItems.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0)
    : selectedTickets.reduce((sum, t) => sum + parseFloat(t.totalCost || 0), 0);

  const walletCredit = Math.max(0, paymentAmount - ticketTotal);
  const ticketCount = hasInvoice
    ? invoiceLineItems.length
    : selectedTickets.length;

  const isValid = hasInvoice
    ? ticketCount > 0
    : (ticketTotal <= paymentAmount && selectedTickets.length > 0);

  // Fetch invoice details or unpaid tickets when dialog opens
  useEffect(() => {
    if (open && payment) {
      if (hasInvoice) {
        fetchInvoiceDetails();
      } else if (payment.organization_id) {
        fetchUnpaidTickets();
      }
    }
  }, [open, payment?.id]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTickets([]);
      setInvoiceDetails(null);
      setError(null);
    }
  }, [open]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getInvoiceById(payment.invoice_id);
      if (response.data?.success) {
        setInvoiceDetails(response.data.data);
      } else {
        setError('Failed to fetch invoice details');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError(err.response?.data?.error || 'Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnpaidTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getUnpaidTickets(
        payment.organization_id,
        payment.typing_center_id
      );

      if (response.data?.success) {
        setUnpaidTickets(response.data.data || []);
      } else {
        setError('Failed to fetch unpaid tickets');
      }
    } catch (err) {
      console.error('Error fetching unpaid tickets:', err);
      setError(err.response?.data?.error || 'Failed to fetch unpaid tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketToggle = (ticket) => {
    setSelectedTickets(prev => {
      const exists = prev.find(t => t.id === ticket.id);
      if (exists) {
        return prev.filter(t => t.id !== ticket.id);
      }

      // Check if adding would exceed payment amount
      const selectedTotal = prev.reduce((sum, t) => sum + parseFloat(t.totalCost || 0), 0);
      const newTotal = selectedTotal + parseFloat(ticket.totalCost || 0);
      if (newTotal > paymentAmount) {
        setError(`Cannot add ticket. Total (${newTotal.toFixed(2)} AED) would exceed payment amount (${paymentAmount.toFixed(2)} AED)`);
        return prev;
      }

      setError(null);
      return [...prev, ticket];
    });
  };

  const handleSelectAll = () => {
    let total = 0;
    const canSelect = [];

    for (const ticket of unpaidTickets) {
      const ticketCost = parseFloat(ticket.totalCost || 0);
      if (total + ticketCost <= paymentAmount) {
        canSelect.push(ticket);
        total += ticketCost;
      }
    }

    setSelectedTickets(canSelect);
    setError(null);
  };

  const handleClearSelection = () => {
    setSelectedTickets([]);
    setError(null);
  };

  const handleSync = async () => {
    if (!isValid) return;

    setSyncing(true);
    setError(null);

    try {
      // For invoice-based: no body needed (backend gets tickets from invoice)
      // For manual: send selected ticket IDs
      const syncData = hasInvoice
        ? {}
        : {
            ticketIds: selectedTickets.map(t => t.id),
            ticketTotal: ticketTotal,
            walletCredit: walletCredit
          };

      const response = await ApiService.syncPaymentToThaklis(payment.id, syncData);

      if (response.data?.success) {
        onSyncComplete && onSyncComplete(response.data);
        onClose();
      } else {
        setError(response.data?.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Error syncing to Thaklis:', err);
      setError(err.response?.data?.error || 'Failed to sync payment to Thaklis');
    } finally {
      setSyncing(false);
    }
  };

  // Render invoice-based confirmation view
  const renderInvoiceView = () => (
    <>
      {/* Invoice Info */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ReceiptIcon color="success" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Invoice Linked
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
          {payment?.invoice_number}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Tickets from this invoice will be marked as paid
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : invoiceDetails ? (
        <>
          {/* Tickets from Invoice */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Tickets to be Paid ({invoiceLineItems.length})
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <List dense disablePadding>
              {invoiceLineItems.map((item, index) => (
                <ListItem
                  key={item.id || index}
                  dense
                  sx={{
                    borderBottom: index < invoiceLineItems.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: '#e8f5e9'
                  }}
                >
                  <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.ticket_number}
                        </Typography>
                        <Chip
                          label={item.service_name || 'Service'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={item.description || item.ticket_snapshot?.applicantName || item.customer_name}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {parseFloat(item.line_total || 0).toFixed(2)} AED
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          <Alert severity="success" sx={{ fontSize: '0.8rem' }}>
            Invoice total matches payment amount. All {invoiceLineItems.length} tickets will be marked as paid when sync is approved.
          </Alert>
        </>
      ) : (
        <Alert severity="error">Failed to load invoice details</Alert>
      )}
    </>
  );

  // Render manual ticket selection view
  const renderManualView = () => (
    <>
      {/* Ticket Selection */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Select Tickets to Mark as Paid
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={loading || unpaidTickets.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Select Max
          </Button>
          <Button
            size="small"
            onClick={handleClearSelection}
            disabled={selectedTickets.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      <Alert severity="warning" sx={{ mb: 2, fontSize: '0.75rem' }}>
        No invoice linked to this payment. Please select tickets manually or link an invoice first for better tracking.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : unpaidTickets.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <ReceiptIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No unpaid tickets found for this organization
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <List dense disablePadding>
            {unpaidTickets.map((ticket, index) => {
              const isSelected = selectedTickets.some(t => t.id === ticket.id);
              const ticketCost = parseFloat(ticket.totalCost || 0);
              const selectedTotal = selectedTickets.reduce((sum, t) => sum + parseFloat(t.totalCost || 0), 0);
              const wouldExceed = !isSelected && (selectedTotal + ticketCost > paymentAmount);

              return (
                <ListItem
                  key={ticket.id}
                  dense
                  button
                  onClick={() => handleTicketToggle(ticket)}
                  disabled={wouldExceed}
                  sx={{
                    borderBottom: index < unpaidTickets.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    opacity: wouldExceed ? 0.5 : 1
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={wouldExceed}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ticket.orgTicketNumber || ticket.ticketNumber}
                        </Typography>
                        <Chip
                          label={ticket.serviceTypeName || ticket.serviceName || 'Service'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={ticket.userName || ticket.customerName || ticket.applicantName || 'Customer'}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: isSelected ? 'primary.main' : 'text.primary',
                      minWidth: 80,
                      textAlign: 'right'
                    }}
                  >
                    {ticketCost.toFixed(2)} AED
                  </Typography>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Sync Payment to Thaklis
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Payment Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Payment Amount
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {paymentAmount.toFixed(2)} AED
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {payment?.organization_name || 'Organization'} - {payment?.payment_reference || payment?.bank_deposit_reference || 'No reference'}
          </Typography>
        </Box>

        {/* Invoice-based or Manual view */}
        {hasInvoice ? renderInvoiceView() : renderManualView()}

        {/* Summary */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {hasInvoice ? 'Invoice' : 'Selected'} Tickets ({ticketCount})
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {ticketTotal.toFixed(2)} AED
            </Typography>
          </Box>

          {walletCredit > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WalletIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Wallet Credit (excess)
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                +{walletCredit.toFixed(2)} AED
              </Typography>
            </Box>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Validation Warning */}
        {!isValid && ticketCount > 0 && !error && !hasInvoice && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Selected tickets total exceeds payment amount
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={syncing}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSync}
          disabled={!isValid || syncing || loading}
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          sx={{ textTransform: 'none' }}
        >
          {syncing ? 'Syncing...' : `Sync ${ticketCount} Tickets to Thaklis`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncToThaklisDialog;
